/**
 * The authenticated session — the single source of who the user is.
 *
 * `GET /api/v1/me` returns the employee, their effective role, their workspace and
 * their capability flags; the nav, every gated action and every drawer tab derive
 * from that response and from nothing else.
 *
 * There is deliberately **no persona switching** here, in any form. The prototype's
 * "Viewing as" control is a review device that must never ship (PRD §"Viewing as"),
 * so this context exposes no way to assume another identity — roles are verified in
 * QA by signing in as real test accounts.
 */
import { MeSchema, type Capabilities, type Me } from "@hrms/shared";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api, isApiError, setCsrfToken, type ApiError } from "./api";
import { identify } from "./analytics";
import { signOutOfFirebase } from "./firebase";

export interface SessionValue {
  /** Null while loading, or when nobody is signed in. */
  me: Me | null;
  capabilities: Capabilities | null;
  csrfToken: string | null;
  loading: boolean;
  error: ApiError | null;
  /** True once `/me` has resolved one way or the other. */
  ready: boolean;
  /**
   * True when the user signed out deliberately, as opposed to arriving signed out.
   * `/signin` needs the distinction: it must not bounce someone straight back in
   * through the developer auto-login they just asked to leave.
   */
  signedOut: boolean;
  /** Re-reads `/me` — call after anything that can change the viewer's own record. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export interface SessionProviderProps {
  children: ReactNode;
  /** Test seam: skips the initial fetch and seeds the context. */
  initialMe?: Me | null;
}

export function SessionProvider({ children, initialMe }: SessionProviderProps) {
  const [me, setMe] = useState<Me | null>(initialMe ?? null);
  const [loading, setLoading] = useState(initialMe === undefined);
  const [ready, setReady] = useState(initialMe !== undefined);
  const [error, setError] = useState<ApiError | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const mounted = useRef(true);
  const client = useQueryClient();

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const next = await api.get("/me", { schema: MeSchema });
      if (!mounted.current) return;
      setMe(next);
      setCsrfToken(next.csrfToken);
      setError(null);
      setSignedOut(false);
    } catch (cause) {
      if (!mounted.current) return;
      setMe(null);
      setCsrfToken(null);
      // 401 is the ordinary signed-out state, not a failure worth showing.
      setError(isApiError(cause) && cause.code !== "unauthenticated" ? cause : null);
    } finally {
      if (mounted.current) {
        setLoading(false);
        setReady(true);
      }
    }
  }, []);

  useEffect(() => {
    if (initialMe !== undefined) {
      if (initialMe) setCsrfToken(initialMe.csrfToken);
      return;
    }
    void load();
  }, [initialMe, load]);

  // Re-identify whenever the viewer's identity or display details change — sign-in,
  // a different person signing in on a shared machine, or a profile edit + refresh().
  // Keyed on the identifying fields, not the object, which every /me refetch replaces.
  const employee = me?.employee;
  useEffect(() => {
    if (employee) identify(employee);
  }, [employee?.id, employee?.workEmail, employee?.name]);

  /**
   * Ends the session everywhere it exists: the server row, the Firebase credential,
   * the CSRF token, this context, and the query cache.
   *
   * Clearing the cache is not housekeeping — BotpressoHR runs on shared machines, and
   * React Query would otherwise hand the next person to sign in the previous person's
   * roster, salary bands and leave requests out of memory before their own `/me`
   * resolves. `clear()` rather than `invalidateQueries()` for the same reason: an
   * invalidated query still renders its old data while it refetches.
   */
  const signOut = useCallback(async () => {
    try {
      // `POST /auth/logout` revokes the session row and expires the cookie. It is the
      // only endpoint that does; there is no `DELETE /auth/session`.
      await api.post("/auth/logout", {});
    } catch {
      // Already expired, or the network is gone. The local state must clear regardless.
    }
    await signOutOfFirebase();
    setCsrfToken(null);
    client.clear();
    if (!mounted.current) return;
    setMe(null);
    setError(null);
    setSignedOut(true);
    setReady(true);
    setLoading(false);
  }, [client]);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      capabilities: me?.capabilities ?? null,
      csrfToken: me?.csrfToken ?? null,
      loading,
      error,
      ready,
      signedOut,
      refresh: load,
      signOut,
    }),
    [me, loading, error, ready, signedOut, load, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used inside <SessionProvider>.");
  return context;
}

/**
 * The signed-in session, for the many components that only render once it exists.
 * Throws if called before `/me` resolved — guard with `useSession().ready` first.
 */
export function useMe(): Me {
  const { me } = useSession();
  if (!me) throw new Error("useMe called without an authenticated session.");
  return me;
}

/**
 * One capability flag. Every gate in the UI reads a named flag rather than
 * comparing role strings, so a permission change is a server-side change only.
 */
export function useCapability(name: keyof Capabilities): boolean {
  const { capabilities } = useSession();
  return capabilities?.[name] ?? false;
}
